// The lenders' MIS on the Lender Hub API (HM2, api2 modules/mis). Shapes: firstfund-contracts/schemas/hub/mis-*.
//   GET /mis/settings · PUT /mis/lenders/{slug}
//   GET / PUT /mis/mailbox · POST /mis/mailbox/test · POST /mis/mailbox/read · GET /mis/files/{id}/preview · GET /mis/files?list=mis|mail · POST /mis/files/{id}/load · /delete · POST /mis/files (base64) · GET /mis/files/{id} · POST /mis/files/{id}/map · /check · /apply · /remove
//   GET /mis/unmatched · GET /mis/missing · POST /mis/missing/cancel · /keep
import type { z } from 'zod'
import type { MisCheck as MisCheckSchema, MisField as MisFieldSchema, MisFile as MisFileSchema, MisFileView as MisFileViewSchema, MisLender as MisLenderSchema, MisMailbox as MisMailboxSchema, MisMapping as MisMappingSchema, MisPreview as MisPreviewSchema, MisMissing as MisMissingSchema, MisRowNote as MisRowNoteSchema, MisSettings as MisSettingsSchema, MisStage as MisStageSchema, MisUnmatchedFile as MisUnmatchedFileSchema } from '@/contract/schemas'
import { api } from '@/lib/hubApi'

export type MisStage = z.infer<typeof MisStageSchema>
export type MisField = z.infer<typeof MisFieldSchema>
export type MisLender = z.infer<typeof MisLenderSchema>
export type MisSettings = z.infer<typeof MisSettingsSchema>
export type MisFile = z.infer<typeof MisFileSchema>
export type MisFileView = z.infer<typeof MisFileViewSchema>
export type MisMapping = z.infer<typeof MisMappingSchema>
export type MisCheck = z.infer<typeof MisCheckSchema>
export type MisRowNote = z.infer<typeof MisRowNoteSchema>
export type MisMissing = z.infer<typeof MisMissingSchema>
export type MisMailbox = z.infer<typeof MisMailboxSchema>
export type MisPreview = z.infer<typeof MisPreviewSchema>
export type MisUnmatchedFile = z.infer<typeof MisUnmatchedFileSchema>
export type DateOrder = MisLender['dateOrder']

export const STAGES: { id: MisStage; label: string }[] = [
  { id: 'applied', label: 'Applied' }, { id: 'kyc', label: 'KYC' }, { id: 'approved', label: 'Approved' }, { id: 'disbursed', label: 'Disbursed' },
  { id: 'rejected', label: 'Rejected' }, { id: 'expired', label: 'Expired / dropped' }, { id: 'cancelled', label: 'Cancelled (after disbursal)' },
]
export const stageLabel = (s: MisStage | null) => (s ? (STAGES.find((x) => x.id === s)?.label ?? s) : 'new')

/** Our MIS fields in the order step 2 shows them; status and one way to match are needed. */
export const FIELDS: { id: MisField; label: string; help: string }[] = [
  { id: 'our_ref', label: 'Our reference', help: 'our lead code (FF-…) or our application ID: what the lender calls ref_id, partner ref, sub_id' },
  { id: 'lender_ref', label: "The lender's reference", help: 'their application / loan number' },
  { id: 'mobile', label: 'Mobile', help: 'plain 10 digits only; a masked mobile cannot match' },
  { id: 'status', label: 'Status', help: 'needed: each word gets one of our stages below' },
  { id: 'status_on', label: 'Status date', help: "the day of the status (else the disbursal day, else the file's day)" },
  { id: 'sanction_amount', label: 'Sanction amount', help: '' },
  { id: 'sanctioned_on', label: 'Sanction date', help: 'when the loan was sanctioned, if the MIS has it' },
  { id: 'disbursed_amount', label: 'Disbursed amount', help: 'needed for a disbursal (or known from an earlier file)' },
  { id: 'disbursed_on', label: 'Disbursal date', help: 'needed for a disbursal (or known from an earlier file)' },
  { id: 'remark', label: 'Remark', help: "the lender's remark or reason (a mobile in it is hidden)" },
]

/** Column kinds never kept as metadata (the Hub refuses them too): personal data. */
export const NEVER_KEPT = ['mobiles', 'masked mobiles', 'PAN numbers', 'e-mails']

/** The India day (YYYY-MM-DD) of a time: a file's arrival day, the latest MIS date it may have. */
export const indiaDay = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })

export const MATCHED_BY: Record<MisCheck['updates'][number]['matchedBy'], string> = { lead_code: 'lead code', partner_ref: 'our application ID', lender_ref: "lender's ref", mobile: 'mobile' }

/** How the MIS inbox names a file's state. A file removed unused is stored as refused with "removed by …": shown as removed. */
export function fileState(f: Pick<MisFile, 'state' | 'refusedReason'>): { label: string; tone: 'info' | 'success' | 'danger' | 'gray'; detail: string | null; group: 'waiting' | 'applied' | 'closed' } {
  if (f.state === 'new') return { label: 'waiting', tone: 'info', detail: null, group: 'waiting' }
  if (f.state === 'used') return { label: 'applied', tone: 'success', detail: null, group: 'applied' }
  if (f.state === 'expired') return { label: 'expired', tone: 'gray', detail: 'nobody applied it in time: deleted', group: 'closed' }
  if (f.refusedReason?.startsWith('removed by ')) return { label: 'removed', tone: 'gray', detail: f.refusedReason, group: 'closed' }
  return { label: 'refused', tone: 'danger', detail: f.refusedReason, group: 'closed' }
}

/** A file as base64 (the browser reads it; the Hub checks what it really is). */
export async function toBase64(file: File) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let bin = ''
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(bin)
}

/** Rows as a CSV the team can send to the lender (references only). */
export function notesCsv(rows: (MisRowNote & { fileName?: string })[]) {
  const q = (v: unknown) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
  const head = ['file', 'row', 'kind', 'our lead code', 'our application ID', 'lender ref', 'status', 'reason']
  return [head.join(','), ...rows.map((r) => [r.fileName ?? '', r.row, r.kind === 'bad' ? 'bad row' : 'not matched', r.leadCode, r.applicationId, r.lenderRef, r.status, r.reason].map(q).join(','))].join('\r\n')
}

export function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }))
  const a = Object.assign(document.createElement('a'), { href: url, download: name })
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export const mis = {
  settings: () => api<MisSettings>('GET', '/mis/settings'),
  saveRule: (slug: string, body: { mailFrom: string[]; subjectWords: string[] }) => api<MisLender>('PUT', `/mis/lenders/${slug}`, body),
  mailbox: () => api<MisMailbox>('GET', '/mis/mailbox'),
  saveMailbox: (body: { host: string; port: number; user: string; password?: string; on: boolean; auto: boolean }) => api<MisMailbox>('PUT', '/mis/mailbox', body),
  testMailbox: (body: { host: string; port: number; user: string; password?: string }) => api<{ ok: boolean; unread?: number; message: string }>('POST', '/mis/mailbox/test', body),
  readMailbox: () => api<{ mails: number; files: number; noLender: number; refused: number }>('POST', '/mis/mailbox/read', {}),
  preview: (id: string) => api<MisPreview>('GET', `/mis/files/${id}/preview`),
  files: (list: 'mis' | 'mail' = 'mis') => api<MisFile[]>('GET', `/mis/files?list=${list}`),
  load: (id: string) => api<MisFile>('POST', `/mis/files/${id}/load`, {}),
  del: (id: string) => api<{ ok: true }>('POST', `/mis/files/${id}/delete`, {}),
  upload: (body: { lenderSlug: string | null; fileName: string; contentBase64: string }) => api<MisFile>('POST', '/mis/files', body),
  file: (id: string) => api<MisFileView>('GET', `/mis/files/${id}`),
  map: (id: string, body: { lenderSlug: string; columns: Partial<Record<MisField, string>>; keep: string[]; words: Record<string, MisStage>; dateOrder: DateOrder; misDay: string | null }) => api<MisMapping>('POST', `/mis/files/${id}/map`, body),
  check: (id: string) => api<MisCheck>('POST', `/mis/files/${id}/check`, {}),
  apply: (id: string) => api<MisCheck>('POST', `/mis/files/${id}/apply`, {}),
  remove: (id: string) => api<MisFile>('POST', `/mis/files/${id}/remove`, {}),
  unmatched: () => api<MisUnmatchedFile[]>('GET', '/mis/unmatched'),
  missing: () => api<MisMissing[]>('GET', '/mis/missing'),
  cancel: (lenderSlug: string, lineKey: string) => api<{ stage: MisStage }>('POST', '/mis/missing/cancel', { lenderSlug, lineKey }),
  keep: (lenderSlug: string, lineKey: string) => api<{ stage: MisStage }>('POST', '/mis/missing/keep', { lenderSlug, lineKey }),
}
